import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Heart, 
  Clock, 
  Users, 
  School, 
  Shield, 
  Award,
  AlertTriangle,
  MessageCircle,
  ExternalLink,
  BookOpen,
  Sparkles,
  ArrowRight,
  Calendar,
  TrendingDown,
  Search,
  Home,
  GraduationCap,
  FileText,
  Bell
} from "lucide-react";
import { Link } from "react-router-dom";

const EdmodoHistory = () => {

  const features = [
    { icon: School, text: "Facebook-like interface designed for K-12 classrooms" },
    { icon: Shield, text: "Safe, walled-off environment with teacher moderation" },
    { icon: FileText, text: "Post announcements, assignments, and share materials" },
    { icon: Award, text: "Badges and gamification to motivate students" },
    { icon: Users, text: "Global teacher communities for professional development" },
    { icon: Heart, text: "Free access that made it accessible to all schools" },
  ];

  const alternatives = [
    { name: "Google Classroom", url: "/online-study/", description: "Assignment posting and student collaboration", internal: true },
    { name: "Microsoft Teams", url: "/online-study/", description: "Enterprise-grade class features", internal: true },
    { name: "Schoology", url: "/online-study/", description: "Dedicated LMS with social feed", internal: true },
    { name: "Canvas/Moodle", url: "/online-study/", description: "Full learning management systems", internal: true },
    { name: "ClassDojo", url: "/online-study/", description: "K-6 parent communication", internal: true },
  ];

  const cswFeatures = [
    "Free virtual study rooms",
    "No time limits",
    "No paywalls",
    "Integrated music and media",
    "Focus timers and task tools",
    "Study + work hybrid spaces",
  ];

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://csw.live/edmodo/history/"
    },
    "headline": "Edmodo: The Classroom Social Network That Shuttered After 15 Years",
    "alternativeHeadline": "Edmodo: From Facebook of Education to Sudden Shutdown",
    "description": "This article recounts the rise and fall of Edmodo, the pioneering K-12 educational social network that connected over 100 million users before its abrupt shutdown in 2022. It explores the platform's innovative features, devoted community, the NetDragon acquisition, and what happened to teachers and students when the service ended.",
    "keywords": "Edmodo, educational technology, K-12, classroom social network, LMS, shutdown, Google Classroom, Microsoft Teams, EdTech, virtual learning",
    "articleSection": "Technology, Education",
    "genre": ["Technology", "Education", "Blog"],
    "inLanguage": "en",
    "wordCount": 2500,
    "about": ["Edmodo", "educational technology", "K-12 learning platforms"],
    "author": {
      "@type": "Organization",
      "name": "CSW Team",
      "url": "https://csw.live"
    },
    "publisher": {
      "@type": "Organization",
      "name": "CSW - Chill Study Work",
      "url": "https://csw.live",
      "logo": {
        "@type": "ImageObject",
        "url": "https://csw.live/og-image.png",
        "width": 1200,
        "height": 630
      }
    },
    "datePublished": "2025-01-11",
    "dateModified": "2025-01-11",
    "image": {
      "@type": "ImageObject",
      "url": "https://csw.live/og-image.png",
      "width": 1200,
      "height": 630
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Edmodo History - The Classroom Social Network That Shuttered"
        description="A retrospective on Edmodo - the Facebook of Education that connected 100 million users before its sudden shutdown in 2022. Discover what happened and where educators found alternatives."
        keywords="edmodo history, edmodo shutdown, edmodo story, classroom social network, educational technology, k-12 learning platform, edmodo alternative"
        canonicalUrl="/edmodo/history/"
        metaImageDescription="edmodo"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Header />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-accent/10" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,hsl(var(--primary)/0.15),transparent_50%)]" />
          
          <div className="container mx-auto px-4 relative z-10">
            {/* Breadcrumb */}
            <nav className="mb-8">
              <ol className="flex items-center gap-2 text-sm text-muted-foreground">
                <li><Link to="/" className="hover:text-primary transition-colors">Home</Link></li>
                <li>/</li>
                <li><Link to="/edmodo/" className="hover:text-primary transition-colors">Edmodo</Link></li>
                <li>/</li>
                <li className="text-foreground">History</li>
              </ol>
            </nav>

            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
                <Calendar className="w-4 h-4" />
                A Retrospective • 2008-2022
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-foreground via-primary to-foreground bg-clip-text text-transparent">
                The Edmodo Story
              </h1>
              
              <p className="text-xl md:text-2xl text-muted-foreground mb-8 leading-relaxed">
                For nearly 15 years, Edmodo was the "Facebook of Education"—a household name in schools worldwide.
              </p>
              
              <p className="text-lg text-muted-foreground/80 max-w-2xl mx-auto">
                When it shut down in 2022, it left <span className="text-foreground font-medium">100+ million users</span> searching for a new home.
              </p>
            </div>

            {/* Hero Image Placeholder */}
            <div className="mt-12 max-w-4xl mx-auto">
              <Card className="p-8 bg-gradient-to-br from-primary/10 to-secondary/10 border-primary/20">
                <div className="aspect-video bg-muted/50 rounded-xl flex items-center justify-center">
                  <GraduationCap className="w-24 h-24 text-muted-foreground/30" />
                </div>
                <p className="text-center text-sm text-muted-foreground mt-3 italic">
                  Edmodo connected classrooms across the world with its social learning platform.
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* Introduction */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <Card className="border-primary/20 bg-gradient-to-br from-background to-primary/5">
                <CardContent className="p-8">
                  <p className="text-lg leading-relaxed text-muted-foreground">
                    "It's so sad!" — the sentiment echoed across the education world when Edmodo announced its closure. Generations of kids had grown up posting on Edmodo boards and earning badges for completing assignments.
                  </p>
                  <p className="text-lg leading-relaxed text-muted-foreground mt-4">
                    This is a look back at what Edmodo was, why it mattered, how it disappeared, and where educators found community after it was gone. For students looking for modern study tools, <Link to="/edmodo/" className="text-primary hover:underline font-medium">check out CSW as an alternative</Link>.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* History & Founders */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-xl bg-primary/10">
                  <BookOpen className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold">History & Founders</h2>
              </div>
              
              <div className="prose prose-lg max-w-none">
                <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                  Edmodo was born in 2008 out of a school district in Illinois. Its founders, <span className="text-foreground font-semibold">Nic Borg</span> and <span className="text-foreground font-semibold">Jeff O'Hara</span>, were a tech-savvy teacher and network administrator who wanted to bridge the gap between how students communicate online and in class.
                </p>
                
                <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                  They noticed students were already on social networks, so why not create a similar space for education? Joined by engineer <span className="text-foreground font-semibold">Crystal Hutter</span>, they launched Edmodo as a free, secure platform for teachers and students to connect.
                </p>

                <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                  Early on, Edmodo gained popularity for its Facebook-like interface that felt familiar to kids, complete with profiles, discussion threads, and the ability to turn in assignments digitally. By focusing on K-12 needs—privacy, moderation, and classroom workflow—Edmodo became a go-to tool alongside learning management systems.
                </p>

                <div className="flex flex-wrap gap-3 mb-8">
                  <a 
                    href="https://www.crunchbase.com/organization/edmodo" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-muted hover:bg-muted/80 text-sm transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Crunchbase Profile
                  </a>
                </div>

                <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                  Over the years, Edmodo evolved from a simple messaging and file-sharing service into a full-fledged educational network with quizzes, parent accounts, and publisher integrations. It attracted over <span className="text-foreground font-semibold">100 million users worldwide</span> by the 2010s—an astonishing reach for an education platform.
                </p>

                <p className="text-xl text-foreground font-medium italic border-l-4 border-primary pl-6">
                  In 2018, Edmodo was acquired by NetDragon, a global education company based in China—a change in ownership that would later play into its shutdown.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Why People Loved It */}
        <section className="py-20 bg-gradient-to-br from-primary/5 via-background to-accent/5">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-xl bg-primary/10">
                  <Heart className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold">Features & Community Appeal</h2>
              </div>
              
              <p className="text-muted-foreground text-lg mb-8">
                What made Edmodo special was how it brought social media-style interaction into learning—safely:
              </p>

              <div className="grid md:grid-cols-2 gap-4 mb-10">
                {features.map((feature, index) => (
                  <Card key={index} className="border-border/50 hover:border-primary/30 transition-colors">
                    <CardContent className="p-5 flex items-start gap-4">
                      <div className="p-2 rounded-lg bg-primary/10 shrink-0">
                        <feature.icon className="w-5 h-5 text-primary" />
                      </div>
                      <p className="text-foreground">{feature.text}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <div className="space-y-6 text-lg text-muted-foreground">
                <p>
                  Teachers could create class groups where they posted announcements, poll questions, quizzes, and assignments. Students joined with a code, ensuring privacy from outsiders. The platform was <span className="text-foreground font-medium">"cyber-safe"</span> and walled-off, giving schools confidence to use it.
                </p>
                <p>
                  A teacher who started using it in 2013 said, "Edmodo platform was one of my best apps when I started teaching." The community aspect wasn't just within each class—Edmodo also hosted global teacher communities where educators shared tips and lesson plans.
                </p>
                <Card className="border-primary/30 bg-primary/5">
                  <CardContent className="p-6">
                    <p className="text-foreground font-medium text-xl">
                      During COVID-19, UNESCO officially recommended Edmodo as a distance-learning solution.
                    </p>
                    <p className="text-muted-foreground mt-2">
                      Its free model helped it spread to developing markets and under-resourced schools.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* The Shutdown */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-xl bg-destructive/10">
                  <AlertTriangle className="w-6 h-6 text-destructive" />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold">The Shutdown</h2>
              </div>

              <div className="relative pl-8 border-l-2 border-destructive/30 space-y-8">
                <div className="relative">
                  <div className="absolute -left-[41px] w-4 h-4 rounded-full bg-destructive" />
                  <p className="text-lg font-medium text-foreground">August 2022</p>
                  <p className="text-muted-foreground mt-2">
                    Right as a new school year was beginning, Edmodo's parent company dropped a bombshell: Edmodo would shut down on September 22, 2022.
                  </p>
                </div>
                
                <div className="relative">
                  <div className="absolute -left-[41px] w-4 h-4 rounded-full bg-destructive/60" />
                  <p className="text-muted-foreground">
                    "It is no longer viable for us to maintain the level of service... we have made the difficult decision to shut down Edmodo.com."
                  </p>
                </div>
                
                <div className="relative">
                  <div className="absolute -left-[41px] w-4 h-4 rounded-full bg-destructive/40" />
                  <p className="text-muted-foreground">
                    Only five weeks notice—teachers were literally setting up fall classes when the announcement hit.
                  </p>
                </div>
              </div>

              <div className="mt-10 space-y-6 text-lg text-muted-foreground">
                <p>
                  Part of the shock was the timing. "There is an abruptness to this," noted one analyst, observing that giving only five weeks notice at back-to-school time left schools scrambling.
                </p>
                <p>
                  NetDragon did not publicly elaborate on reasons, but it was widely speculated that the costs of running a free platform at such scale, combined with low revenue, made it unsustainable. Google Classroom, Microsoft Teams, and others had grown in adoption, squeezing Edmodo's share.
                </p>
                <p>
                  Edmodo informed users that <span className="text-foreground font-medium">all accounts and content would be permanently deleted</span> upon closure. Years of students' messages, discussion threads, and assignment feedback would vanish irretrievably.
                </p>

                <div className="flex flex-wrap gap-3">
                  <Link 
                    to="/blog/how-to-stop-procrastination/"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-muted hover:bg-muted/80 text-sm transition-colors hover:text-primary"
                  >
                    <ArrowRight className="w-4 h-4" />
                    How to Stop Procrastination
                  </Link>
                  <Link 
                    to="/blog/top-10-digital-tools-for-academic-success/"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-muted hover:bg-muted/80 text-sm transition-colors hover:text-primary"
                  >
                    <ArrowRight className="w-4 h-4" />
                    Top 10 Digital Tools for Success
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Community Reaction */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-xl bg-primary/10">
                  <MessageCircle className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold">Community Reaction & Impact</h2>
              </div>

              <div className="space-y-6 text-lg text-muted-foreground">
                <p>
                  The reaction was widespread disappointment and even outrage in the education community. On social media, teachers vented about losing a trusted platform with little warning.
                </p>
                
                <Card className="border-border/50 bg-card/50">
                  <CardContent className="p-6">
                    <p className="text-foreground italic text-xl">
                      "After more than a decade… this news hit hard."
                    </p>
                    <p className="text-muted-foreground text-sm mt-2">— EdWeek, reporting on educator reactions</p>
                  </CardContent>
                </Card>

                <p>
                  Some criticized the timing: "Why… coming so close to the date of shutdown?" one teacher complained. Others lamented the loss of features that had no one-to-one replacement in other tools.
                </p>

                <p>
                  A common sentiment: Edmodo had been a uniquely teacher-and-student-friendly space, and its shutdown felt like a betrayal of trust. "A shutdown makes users feel betrayed… it makes it harder for tech companies to pitch new products," wrote Ars Technica.
                </p>

                <Card className="border-primary/30 bg-gradient-to-br from-primary/5 to-secondary/5">
                  <CardContent className="p-6">
                    <p className="text-foreground font-medium">
                      Beyond files, "What I cannot back up are all the messages, announcements, and my interactions with my students."
                    </p>
                    <p className="text-muted-foreground text-sm mt-2">— An educator from the Philippines</p>
                  </CardContent>
                </Card>

                <p>
                  The incident sparked discussions in EdTech circles about reliance on free platforms. For Edmodo's part, their final message thanked the community "for your continued loyalty and contribution."
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* What Remains */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-xl bg-primary/10">
                  <Search className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold">What Remains</h2>
              </div>

              <div className="space-y-6 text-lg text-muted-foreground">
                <p>
                  After Edmodo's shutdown, little remains of the platform in a usable form. The website now simply displays a notice of closure. All user data has been purged for privacy.
                </p>

                <p>
                  However, Edmodo's legacy survives in indirect ways. The concept of a safe K-12 social network is something Google and Microsoft have taken to heart—Google Classroom added more social-stream features, and Microsoft's Flip (formerly Flipgrid) took inspiration in connecting students.
                </p>

                <p>
                  Importantly, the community of teachers that thrived on Edmodo didn't disappear. Many migrated their professional groups to other forums. <Link to="/edmodo/" className="text-primary hover:underline font-medium">CSW provides a modern alternative</Link> for students seeking collaborative study environments.
                </p>

                <Card className="border-primary/30 bg-primary/5">
                  <CardContent className="p-6">
                    <p className="text-foreground font-medium text-xl">
                      Edmodo's commitment to keeping student data private even in death set a standard.
                    </p>
                    <p className="text-muted-foreground mt-2">
                      Observers gave the company credit for "choosing not to sell off data" as it closed—a move that highlighted ethical considerations for edtech closures.
                    </p>
                  </CardContent>
                </Card>

                <p>
                  In the hall of fame of edtech, Edmodo will be remembered as a pioneer that touched over 100 million lives—a significant legacy by any measure.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Alternatives Section */}
        <section className="py-20 bg-gradient-to-br from-muted/50 to-background">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-xl bg-primary/10">
                  <Sparkles className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold">Alternatives & Successors</h2>
              </div>

              <p className="text-muted-foreground text-lg mb-8">
                In the wake of Edmodo's demise, teachers and schools sought out other platforms to fill the void:
              </p>

              <div className="grid md:grid-cols-2 gap-4 mb-10">
                {alternatives.map((alt, index) => (
                  <Link 
                    key={index}
                    to={alt.url}
                    className="block"
                  >
                    <Card className="border-border/50 hover:border-primary/30 transition-all hover:shadow-md h-full">
                      <CardContent className="p-5">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-foreground font-medium">{alt.name}</p>
                            <p className="text-muted-foreground text-sm mt-1">{alt.description}</p>
                          </div>
                          <ArrowRight className="w-4 h-4 text-muted-foreground" />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>

              <p className="text-muted-foreground text-lg">
                No single tool matched Edmodo's full mix of features and community feel exactly. Often, educators ended up using a combination of tools. By 2023, most Edmodo refugees had landed on their feet with new digital toolkits.
              </p>
            </div>
          </div>
        </section>

        {/* CSW CTA Section */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <Card className="border-primary/30 bg-gradient-to-br from-primary/10 to-secondary/10 overflow-hidden">
                <CardContent className="p-8 md:p-12">
                  <div className="text-center space-y-6">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/20 text-primary text-sm font-medium">
                      <Home className="w-4 h-4" />
                      Find Your New Study Home
                    </div>
                    
                    <h2 className="text-3xl md:text-4xl font-bold text-foreground">
                      Looking for a Modern Study Platform?
                    </h2>
                    
                    <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                      CSW carries forward Edmodo's mission of connected learning with free virtual study rooms, collaborative features, and productivity tools—all designed for today's learners.
                    </p>

                    <div className="flex flex-wrap justify-center gap-3">
                      {cswFeatures.map((feature, index) => (
                        <span 
                          key={index}
                          className="px-3 py-1 rounded-full bg-muted text-sm text-muted-foreground"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                      <Button 
                        size="lg"
                        className="bg-primary hover:bg-primary/90 text-primary-foreground"
                        asChild
                      >
                        <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                          Try CSW Free <ArrowRight className="ml-2 w-4 h-4" />
                        </a>
                      </Button>
                      <Button 
                        size="lg"
                        variant="outline"
                        asChild
                      >
                        <Link to="/edmodo/">
                          Learn More About CSW
                        </Link>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Related Articles */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h3 className="text-2xl font-bold mb-6">Related Articles</h3>
              <div className="grid md:grid-cols-3 gap-4">
                <Link to="/studyverse/history/" className="group">
                  <Card className="h-full border-border/50 hover:border-primary/30 transition-colors">
                    <CardContent className="p-5">
                      <p className="font-medium text-foreground group-hover:text-primary transition-colors">Studyverse History</p>
                      <p className="text-sm text-muted-foreground mt-1">The rise and fall of virtual study halls</p>
                    </CardContent>
                  </Card>
                </Link>
                <Link to="/groove/history/" className="group">
                  <Card className="h-full border-border/50 hover:border-primary/30 transition-colors">
                    <CardContent className="p-5">
                      <p className="font-medium text-foreground group-hover:text-primary transition-colors">Groove History</p>
                      <p className="text-sm text-muted-foreground mt-1">Virtual coworking's emotional journey</p>
                    </CardContent>
                  </Card>
                </Link>
                <Link to="/blog/benefits-of-online-learning-community/" className="group">
                  <Card className="h-full border-border/50 hover:border-primary/30 transition-colors">
                    <CardContent className="p-5">
                      <p className="font-medium text-foreground group-hover:text-primary transition-colors">Online Learning Communities</p>
                      <p className="text-sm text-muted-foreground mt-1">Why connection matters in education</p>
                    </CardContent>
                  </Card>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default EdmodoHistory;
